import { Button } from '@kizen/kds/Button';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import Notice from '__components/Kizen/Notice';
import BasicModal from '__components/Modals/presets/BasicModal';
import useModal from '__components/Modals/useModal';
import { MarketplacePlugin } from '__pages/Marketplace/types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PluginService from 'services/PluginService';
import { useToast } from '__components/ToastProvider';
import { toastVariant } from '__components/ToastProvider';
import { useInfiniteQuery } from '@tanstack/react-query';
import { PLUGINS } from '__queries/query-keys';
import Loader from '__components/Kizen/Loader';
import { markdownToHtml } from '@kizen/parse-md';
import { markdownPreviewStyles } from '__components/WYSIWYG/MarkdownEditor/styles';
import styled from '@emotion/styled';
import { InstalledPlugin } from 'ts-components/Plugins/types';

const MarkdownContainer = styled.div`
  ${markdownPreviewStyles({ color: 'var(--color-font-primary)' })}
  & * {
    font-size: 14px;
  }
`;

type VersionsPanelProps = {
  installedPlugin?: InstalledPlugin;
  plugin: MarketplacePlugin;
  businessPluginAppsByApiName: Record<string, any>;
  refetch: () => Promise<unknown>;
};

export const VersionsPanel = ({
  installedPlugin,
  plugin,
  businessPluginAppsByApiName,
  refetch,
}: VersionsPanelProps) => {
  const { t } = useTranslation();
  const scrollMarkerRef = useRef<HTMLDivElement>(null);

  const [showToast] = useToast();

  const infiniteQueryFn = useCallback(
    async ({ pageParam = '1' }) => {
      const response = await PluginService.getReleaseNotes(
        plugin.api_name,
        pageParam
      );

      const convertedResults = await Promise.all(
        response.results.map(async (entry: any) => {
          const content = entry.content;
          const html = await markdownToHtml(content);
          return {
            ...entry,
            html,
          };
        })
      );

      return {
        ...response,
        results: convertedResults,
      };
    },
    [plugin.api_name]
  );

  const infiniteQuery = useInfiniteQuery({
    initialPageParam: '1',
    queryKey: PLUGINS.GET_RELEASE_NOTES(plugin.api_name),
    queryFn: infiniteQueryFn,
    getNextPageParam: (lastPage: any) => {
      return lastPage?.next
        ? new URL(lastPage.next).searchParams.get('page')
        : undefined;
    },
    getPreviousPageParam: (previousPage: any) => {
      return previousPage?.prev
        ? new URL(previousPage.prev).searchParams.get('page')
        : undefined;
    },
    retry: false,
    throwOnError: false,
    staleTime: Infinity,
  });

  const {
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    data: changelogData,
    isFetching: fetching,
    isLoading,
  } = infiniteQuery;

  const onScrollThreshold = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  useEffect(() => {
    const el = scrollMarkerRef.current;
    // handle scroll marker into view
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !isFetchingNextPage && !fetching) {
        onScrollThreshold();
      }
    });

    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
  }, [isFetchingNextPage, fetching, onScrollThreshold]);

  const allChangelogData = useMemo(() => {
    const entries: any = [];

    changelogData?.pages.forEach((page) => {
      entries.push(...page.results);
    });

    return entries;
  }, [changelogData]);

  const [confirmationModalProps, , confirmationModal] = useModal({
    handleSubmit: async () => {
      if (installedPlugin) {
        const businessPluginApp = businessPluginAppsByApiName[plugin.api_name];
        try {
          await PluginService.upgradeBusinessPluginApp(
            businessPluginApp.id,
            installedPlugin.upgradeable
          );
          await refetch();
          showToast({
            message: t('Plugin upgraded successfully'),
            variant: toastVariant.SUCCESS,
          });
        } catch (ex: any) {
          showToast({
            message: ex.message ?? t('Failed to upgrade plugin'),
            variant: toastVariant.FAILURE,
          });
        }
      } else {
        showToast({
          message: t('Cannot upgrade an uninstalled plugin'),
          variant: toastVariant.FAILURE,
        });
      }
    },
  });

  const handleUpgradeClick = useCallback(() => {
    confirmationModal.show();
  }, [confirmationModal]);

  const installedVersion = installedPlugin?.version;

  return (
    <div>
      <Notice>
        {!installedPlugin ? (
          <Typography>{t('App not installed')}</Typography>
        ) : (
          <Typography>
            {t('Installed version')}: <strong>{installedVersion}</strong>
          </Typography>
        )}
        {installedPlugin?.upgradeable ? (
          <>
            <Spacer size={15} />
            <Typography>
              {t('Upgrade available to version')}:{' '}
              <strong>{installedPlugin.upgradeable}</strong>
            </Typography>
            <Spacer size={15} />
            <Button color="primary" onClick={handleUpgradeClick}>
              {t('Upgrade Now')}
            </Button>
          </>
        ) : installedPlugin ? (
          <>
            <Spacer size={15} />
            <Typography>
              {t('{{name}} is currently up to date', {
                name: installedPlugin?.name,
              })}
            </Typography>
          </>
        ) : null}
      </Notice>
      <div>
        <Spacer size={20} />

        <Typography variant="header" size="2xl" weight="semibold">
          {t('Version History')}
        </Typography>
        <Spacer size={20} />
        {allChangelogData?.map((entry: any, index: number) => {
          const isInstalledVersion = entry.version === installedVersion;
          const isLatestVersion = index === 0;
          const isLastInList = index === allChangelogData.length - 1;

          return (
            <div key={entry.version}>
              <div className="flex items-center gap-10">
                <Typography variant="header" size="xl" weight="semibold">
                  {entry.version}
                </Typography>
                {isInstalledVersion ? (
                  <Typography
                    color="button-secondary-default"
                    weight="semibold"
                  >
                    Current Version
                  </Typography>
                ) : isLatestVersion ? (
                  <Typography color="button-primary-default" weight="semibold">
                    Latest Version
                  </Typography>
                ) : null}
              </div>
              <Spacer size={15} />
              {entry.content ? (
                <MarkdownContainer
                  className="kds"
                  dangerouslySetInnerHTML={{ __html: entry.html }}
                />
              ) : (
                <Typography color="font-placeholder">
                  {t('No details provided')}
                </Typography>
              )}
              {!isLastInList ? <Spacer size={20} /> : null}
            </div>
          );
        })}
        {allChangelogData?.length === 0 && !isLoading ? (
          <Typography>{t('No version history available')}</Typography>
        ) : null}
        {isFetchingNextPage || isLoading || hasNextPage ? (
          <Loader loading paddingTop={0} />
        ) : null}
        <div ref={scrollMarkerRef} />
      </div>
      <BasicModal
        {...confirmationModalProps}
        heading={t('Confirm Upgrade to Version {{version}}', {
          version: installedPlugin?.upgradeable,
        })}
        buttonText={t('Upgrade')}
      >
        <div>
          <Typography>
            {t(
              'Are you sure you want to upgrade {{name}} from version {{oldVersion}} to {{newVersion}}? This action may require configuration changes.',
              {
                name: installedPlugin?.name,
                oldVersion: installedPlugin?.version,
                newVersion: installedPlugin?.upgradeable,
              }
            )}
          </Typography>
          <Spacer size={5} />
        </div>
      </BasicModal>
    </div>
  );
};
